import { Component, signal, computed, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { UserNotificationService } from '../../services/user-notification.service';
import { DownloadJobService } from '../../services/download-job.service';
import { ToastService } from '../../services/toast.service';
import { AuthService } from '../../services/auth.service';
import { UserNotificationModel, UserNotificationType, UserNotificationData } from '../../../models';
import { Subscription } from 'rxjs';
import * as signalR from '@microsoft/signalr';

import { logger } from '../../../helpers/logger';
@Component({
  selector: 'eng-notifications',
  imports: [CommonModule, NgbDropdownModule],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss',
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private userNotificationService = inject(UserNotificationService);
  private downloadJobService = inject(DownloadJobService);
  private toastService = inject(ToastService);
  private authService = inject(AuthService);
  private router = inject(Router);

  public readonly userNotificationType = UserNotificationType;
  private authSubscription?: Subscription;
  private hubConnection?: signalR.HubConnection;

  // Signals for reactive state management
  public notifications = signal<UserNotificationModel[]>([]);
  public unreadCount = signal<number>(0);
  public isLoading = signal<boolean>(false);

  // Computed properties
  public readonly hasNotifications = computed(() => this.notifications().length > 0);
  public readonly hasUnreadNotifications = computed(() => this.unreadCount() > 0);

  ngOnInit(): void {
    // Request browser notification permission on component init
    this.toastService.requestBrowserNotificationPermission();

    // Set up callback for when browser notifications are closed
    this.toastService.setBrowserNotificationClosedCallback((notificationId: string) => {
      this.onBrowserNotificationClosed(notificationId);
    });

    // Wait for authentication before loading notifications
    // This prevents 401 errors during initial page load
    this.authSubscription = this.authService.getAuthenticationStatus().subscribe(isAuthenticated => {
      if (isAuthenticated) {
        // Load initial notifications only once on page refresh
        // SignalR will handle all real-time updates after this
        this.loadNotifications();
        this.loadUnreadCount();
        this.setupSignalRConnection();
      }
    });
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
    this.hubConnection?.stop();
  }

  private setupSignalRConnection(): void {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl('/hubs/user-notifications', {
        withCredentials: true,
      })
      .build();

    // Listen for new user notifications
    this.hubConnection.on('UserNotificationCreated', (notification: UserNotificationModel) => {
      // Show toast notification for 1 second with browser notification
      this.showToastForNotification(notification);

      // Reload notifications when a new one is created
      this.loadNotifications();
      this.loadUnreadCount();
    });

    // Listen for deleted user notifications (for superseded download notifications)
    this.hubConnection.on('UserNotificationDeleted', (data: { notificationId: string; downloadJobId: string }) => {
      // Add a small delay to ensure proper processing order
      setTimeout(() => {
        // Close the corresponding browser notification
        this.toastService.closeBrowserNotification(data.notificationId);

        // Remove from local state if it exists
        const updatedNotifications = this.notifications().filter(n => n.id !== data.notificationId);
        this.notifications.set(updatedNotifications);

        // Update unread count
        this.loadUnreadCount();
      }, 50); // 50ms delay to ensure proper processing
    });

    // Start the connection
    this.hubConnection.start().catch(err => {
      logger.error('Error starting SignalR connection:', err);
    });
  }

  private loadNotifications(): void {
    this.userNotificationService.getUserNotifications(10).subscribe({
      next: notifications => {
        // Convert date strings to Date objects
        const processedNotifications = notifications.map(n => ({
          ...n,
          createdDate: new Date(n.createdDate),
          readDate: n.readDate ? new Date(n.readDate) : undefined,
          expiresDate: n.expiresDate ? new Date(n.expiresDate) : undefined,
        }));
        this.notifications.set(processedNotifications);
      },
      error: error => {
        logger.error('Failed to load notifications:', error);
      },
    });
  }

  private loadUnreadCount(): void {
    this.userNotificationService.getUnreadCount().subscribe({
      next: response => {
        this.unreadCount.set(response.unreadCount);
      },
      error: error => {
        logger.error('Failed to load unread count:', error);
      },
    });
  }

  public onNotificationClick(notification: UserNotificationModel): void {
    // Mark as read if not already read
    if (!notification.isRead) {
      this.markNotificationAsRead(notification);
    }

    // Perform the notification action based on type
    this.performNotificationAction(notification);
  }

  private markNotificationAsRead(notification: UserNotificationModel): void {
    this.userNotificationService.markAsRead(notification.id).subscribe({
      next: () => {
        // Update local state
        const updatedNotifications = this.notifications().map(n =>
          n.id === notification.id ? { ...n, isRead: true, readDate: new Date() } : n
        );
        this.notifications.set(updatedNotifications);
        this.unreadCount.update(count => Math.max(0, count - 1));

        // Close corresponding browser notification
        this.toastService.closeBrowserNotification(notification.id);
      },
      error: error => {
        logger.error('Failed to mark notification as read:', error);
      },
    });
  }

  private onBrowserNotificationClosed(notificationId: string): void {
    // Find the notification and mark it as read if it's not already read
    const notification = this.notifications().find(n => n.id === notificationId);
    if (notification && !notification.isRead) {
      // Mark as read without closing browser notification (since it's already closed)
      this.userNotificationService.markAsRead(notification.id).subscribe({
        next: () => {
          // Update local state
          const updatedNotifications = this.notifications().map(n =>
            n.id === notification.id ? { ...n, isRead: true, readDate: new Date() } : n
          );
          this.notifications.set(updatedNotifications);
          this.unreadCount.update(count => Math.max(0, count - 1));
        },
        error: error => {
          logger.error('Failed to mark notification as read:', error);
        },
      });
    }
  }

  private performNotificationAction(notification: UserNotificationModel): void {
    if (notification.type === UserNotificationType.Download) {
      this.handleDownloadNotification(notification);
    } else if (notification.type === UserNotificationType.ProjectUpdate) {
      this.handleProjectUpdateNotification(notification);
    }
    // Add other notification type handlers here as needed
  }

  private handleDownloadNotification(notification: UserNotificationModel): void {
    try {
      const data: UserNotificationData = notification.data ? JSON.parse(notification.data) : {};

      // Only allow action for completed downloads
      if (data.downloadJobId && data.status === 'Completed') {
        // Trigger download
        this.downloadJobService.downloadFile(data.downloadJobId);
      }
      // For Queued and Processing notifications, do nothing (no action)
    } catch (error) {
      logger.error('Failed to parse notification data:', error);
    }
  }

  private handleProjectUpdateNotification(notification: UserNotificationModel): void {
    try {
      const data: UserNotificationData = notification.data ? JSON.parse(notification.data) : {};

      // Build route dynamically based on notification data
      if (data['projectId']) {
        let route: string;

        // If postId is present, navigate to the specific post
        if (data['postId']) {
          route = `/projects/${data['projectId']}/post/${data['postId']}`;
        } else {
          // Otherwise navigate to the project's posts page
          route = `/projects/${data['projectId']}/post`;
        }

        this.router.navigateByUrl(route);
      }
    } catch (error) {
      logger.error('Failed to parse notification data:', error);
    }
  }

  public isNotificationClickable(notification: UserNotificationModel): boolean {
    if (notification.type === UserNotificationType.Download) {
      try {
        const data: UserNotificationData = notification.data ? JSON.parse(notification.data) : {};
        // Only completed downloads are clickable
        return data.status === 'Completed';
      } catch (error) {
        return false;
      }
    }
    // Other notification types are clickable by default
    return true;
  }

  public showProgressIndicator(notification: UserNotificationModel): boolean {
    if (notification.type === UserNotificationType.Download) {
      try {
        const data: UserNotificationData = notification.data ? JSON.parse(notification.data) : {};
        // Show progress indicator for queued and processing downloads
        return data.status === 'Queued' || data.status === 'Processing';
      } catch (error) {
        return false;
      }
    }
    return false;
  }

  public removeNotification(notification: UserNotificationModel, event: Event): void {
    // Stop event propagation to prevent triggering the notification click
    event.stopPropagation();

    this.userNotificationService.deleteNotification(notification.id).subscribe({
      next: () => {
        // Remove from local state
        const updatedNotifications = this.notifications().filter(n => n.id !== notification.id);
        this.notifications.set(updatedNotifications);

        // Update unread count if the notification was unread
        if (!notification.isRead) {
          this.unreadCount.update(count => Math.max(0, count - 1));
        }
      },
      error: error => {
        logger.error('Failed to remove notification:', error);
      },
    });
  }

  public clearAllRead(): void {
    this.userNotificationService.deleteAllReadNotifications().subscribe({
      next: () => {
        // Remove read notifications from local state
        const unreadNotifications = this.notifications().filter(n => !n.isRead);
        this.notifications.set(unreadNotifications);
      },
      error: error => {
        logger.error('Failed to clear read notifications:', error);
      },
    });
  }

  public markAllAsRead(): void {
    this.userNotificationService.markAllAsRead().subscribe({
      next: () => {
        // Close all browser notifications for unread notifications
        this.notifications().forEach(notification => {
          if (!notification.isRead) {
            this.toastService.closeBrowserNotification(notification.id);
          }
        });

        // Update all notifications to read in local state
        const updatedNotifications = this.notifications().map(n => ({
          ...n,
          isRead: true,
          readDate: new Date(),
        }));
        this.notifications.set(updatedNotifications);
        this.unreadCount.set(0);
      },
      error: error => {
        logger.error('Failed to mark all as read:', error);
      },
    });
  }

  public getNotificationIcon(notification: UserNotificationModel): string {
    switch (notification.type) {
      case UserNotificationType.Download:
        return 'bi-download';
      case UserNotificationType.System:
        return 'bi-info-circle';
      case UserNotificationType.Alert:
        return 'bi-exclamation-triangle';
      case UserNotificationType.ProjectUpdate:
        return 'bi-folder';
      case UserNotificationType.CommentReply:
        return 'bi-chat';
      case UserNotificationType.FileProcessed:
        return 'bi-file-check';
      default:
        return 'bi-bell';
    }
  }

  public getNotificationClass(notification: UserNotificationModel): string {
    const baseClass = 'dropdown-item notification-item';
    const readClass = notification.isRead ? 'read' : 'unread';
    const typeClass = `notification-${notification.type.toLowerCase()}`;

    return `${baseClass} ${readClass} ${typeClass}`;
  }

  private showToastForNotification(notification: UserNotificationModel): void {
    // Determine toast type based on notification type
    let toastType: 'success' | 'error' | 'warning' | 'info' = 'info';

    switch (notification.type) {
      case UserNotificationType.Download:
        // Check if it's a completed download
        try {
          const data: UserNotificationData = notification.data ? JSON.parse(notification.data) : {};
          if (data.status === 'Completed') {
            toastType = 'success';
          } else if (data.status === 'Failed') {
            toastType = 'error';
          } else {
            toastType = 'info';
          }
        } catch {
          toastType = 'info';
        }
        break;
      case UserNotificationType.Alert:
        toastType = 'warning';
        break;
      case UserNotificationType.System:
        toastType = 'info';
        break;
      case UserNotificationType.ProjectUpdate:
      case UserNotificationType.CommentReply:
      case UserNotificationType.FileProcessed:
        toastType = 'success';
        break;
      default:
        toastType = 'info';
    }

    // Show toast notification for 1 second with browser notification, linked to notification ID
    this.toastService.notification(
      notification.title,
      notification.message,
      toastType,
      (notification as any).notificationId || notification.id // Pass notification ID for bidirectional linking
    );
  }

  public getRelativeTime(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) {
      return 'just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    }
  }
}
